import { useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Play, Pause, X, Headphones } from "lucide-react";
import { useAudio } from "@/contexts/AudioContext";
import { Slider } from "@/components/ui/slider";

const BARS = 24;

// Module-level audio nodes so they survive component remounts
let _audioCtx: AudioContext | null = null;
let _analyser: AnalyserNode | null = null;
let _connectedElement: HTMLAudioElement | null = null;

function getAnalyser(audio: HTMLAudioElement): AnalyserNode | null {
  if (_analyser && _connectedElement === audio) return _analyser;
  try {
    if (!_audioCtx) _audioCtx = new AudioContext();
    const source = _audioCtx.createMediaElementSource(audio);
    const analyser = _audioCtx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);
    analyser.connect(_audioCtx.destination);
    _analyser = analyser;
    _connectedElement = audio;
    return analyser;
  } catch {
    // already connected — try to reuse existing
    return _analyser;
  }
}

const AudioVisualizer = ({ isPlaying, audioRef }: { isPlaying: boolean; audioRef: React.RefObject<HTMLAudioElement> }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const smoothedRef = useRef<Float32Array>(new Float32Array(BARS).fill(0));

  useEffect(() => {
    if (isPlaying && audioRef.current) {
      getAnalyser(audioRef.current);
      if (_audioCtx?.state === 'suspended') _audioCtx.resume();
    }
  }, [isPlaying, audioRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const W = 120;
    const H = 28;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const smoothed = smoothedRef.current;
    const barW = W / BARS - 1.2;
    const gap = 1.2;

    const draw = () => {
      ctx.clearRect(0, 0, W * 2, H * 2);
      const analyser = _analyser;
      const now = Date.now();
      const mid = H / 2;
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let data: any = null;
      if (analyser && isPlaying) {
        data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
      }

      for (let i = 0; i < BARS; i++) {
        let target: number;
        if (data) {
          const idx = Math.floor((i / BARS) * data.length * 0.7);
          target = data[idx] / 255;
        } else {
          target = isPlaying
            ? Math.sin(i * 0.4 + now * 0.003) * 0.3 + 0.15
            : Math.sin(i * 0.5 + now * 0.001) * 0.08 + 0.06;
        }

        smoothed[i] += (target - smoothed[i]) * 0.22;
        const h = Math.max(2, smoothed[i] * H * 0.85);
        const x = i * (barW + gap);

        // Bar with rounded caps
        const radius = Math.min(barW / 2, 1.5);
        const y1 = mid - h / 2;
        const y2 = mid + h / 2;

        ctx.beginPath();
        ctx.moveTo(x + radius, y1);
        ctx.lineTo(x + barW - radius, y1);
        ctx.quadraticCurveTo(x + barW, y1, x + barW, y1 + radius);
        ctx.lineTo(x + barW, y2 - radius);
        ctx.quadraticCurveTo(x + barW, y2, x + barW - radius, y2);
        ctx.lineTo(x + radius, y2);
        ctx.quadraticCurveTo(x, y2, x, y2 - radius);
        ctx.lineTo(x, y1 + radius);
        ctx.quadraticCurveTo(x, y1, x + radius, y1);
        ctx.closePath();

        // Color based on amplitude
        const intensity = smoothed[i];
        const alpha = 0.4 + intensity * 0.5;
        ctx.fillStyle = `hsla(25, 60%, ${55 + intensity * 15}%, ${alpha})`;
        ctx.fill();
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying]);

  return <canvas ref={canvasRef} style={{ width: 120, height: 28 }} className="block" />;
};

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const AudioPlayer = () => {
  const { isPlaying, isVisible, currentTime, duration, togglePlay, close, seek, audioRef } = useAudio();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => {
      close();
      if (pathname !== "/book-a-call") navigate("/book-a-call");
    };
    audio.addEventListener("ended", onEnded);
    return () => audio.removeEventListener("ended", onEnded);
  }, [audioRef, close, navigate, pathname]);

  if (!isVisible) return null;

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="fixed bottom-5 right-5 z-50 rounded-2xl shadow-2xl shadow-black/40 animate-in slide-in-from-bottom-4 fade-in duration-400 overflow-hidden">
      <div className="absolute inset-0 bg-card/90 backdrop-blur-2xl rounded-2xl" />
      <div className="absolute inset-0 border border-white/[0.08] rounded-2xl" />
      
      <div className="relative">
        <div className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5">
          {/* Play/Pause with progress ring */}
          <button
            onClick={togglePlay}
            className="relative flex-shrink-0 w-9 h-9 rounded-full bg-primary/12 text-primary flex items-center justify-center hover:bg-primary/20 smooth"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" stroke="hsl(var(--border))" strokeWidth="1.5" opacity="0.2" />
              <circle
                cx="18" cy="18" r="16" fill="none"
                stroke="hsl(var(--primary))" strokeWidth="2"
                strokeDasharray={`${2 * Math.PI * 16}`}
                strokeDashoffset={`${2 * Math.PI * 16 * (1 - progress)}`}
                strokeLinecap="round"
                className="smooth"
              />
            </svg>
            {isPlaying ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
          </button>

          {/* Visualizer */}
          <AudioVisualizer isPlaying={isPlaying} audioRef={audioRef} />

          {/* Time */}
          <span className="text-[10px] text-muted-foreground/60 tabular-nums whitespace-nowrap">
            {formatTime(currentTime)}
          </span>

          {/* Close */}
          <button
            onClick={close}
            className="w-5 h-5 rounded-full text-muted-foreground/40 hover:text-foreground flex items-center justify-center smooth"
            aria-label="Close player"
          >
            <X size={10} />
          </button>
        </div>

        {/* Thin timeline bar */}
        <div
          className="h-[2px] bg-border/20 cursor-pointer"
          onClick={(e) => {
            const audio = audioRef.current;
            const dur = audio?.duration || duration;
            if (!dur || dur <= 0) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            seek(pct * dur);
          }}
        >
          <div
            className="h-full bg-primary/70 smooth"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
